import { preferOrgLocalDateTime } from '@/lib/org-date-locale'
import { getJobCategoryColor, resolveJobCategoryKey } from '@/lib/job-list-display'
import {
  resolveWorkDayEntryStatusFromEntry,
  type WorkDayEntryStatus,
} from '@/lib/workday-entry-status'
import type { Job, WorkDayEntry } from '@/types'

const DAY_MINUTES = 24 * 60

export type WorkDayCategoryTimelineEntry = {
  entry: WorkDayEntry
  categoryKey: string
  categoryLabel: string
  workDateKey: string
  startMinutes: number
  endMinutes: number
}

export type WorkDayCategoryTimelineRow = {
  categoryKey: string
  categoryLabel: string
  color: ReturnType<typeof getJobCategoryColor>
  entriesByDate: Map<string, WorkDayCategoryTimelineEntry[]>
}

export function toIsoDateKey(value: Date) {
  const localDate = new Date(value)
  localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset())
  return localDate.toISOString().slice(0, 10)
}

export function parseIsoDateKey(value: string) {
  return new Date(`${value}T12:00:00`)
}

export function getWeekStartMonday(date: Date) {
  const normalized = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const weekday = normalized.getDay()
  const diff = weekday === 0 ? -6 : 1 - weekday
  normalized.setDate(normalized.getDate() + diff)
  return normalized
}

export function getWeekDays(weekStart: Date) {
  return Array.from({ length: 7 }, (_, index) => {
    const day = new Date(weekStart)
    day.setDate(weekStart.getDate() + index)
    return day
  })
}

export function formatWeekRangeLabel(weekStart: Date, locale = 'en-US') {
  const weekEnd = new Date(weekStart)
  weekEnd.setDate(weekStart.getDate() + 6)

  const startLabel = weekStart.toLocaleDateString(locale, { month: 'short', day: 'numeric' })
  const endLabel = weekEnd.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: weekStart.getFullYear() === weekEnd.getFullYear() ? undefined : 'numeric',
  })

  return `${startLabel} – ${endLabel}`
}

function readDateKeyFromValue(value?: string | null) {
  if (!value?.trim()) {
    return null
  }

  const trimmed = value.trim()
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    return trimmed.slice(0, 10)
  }

  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }

  return toIsoDateKey(parsed)
}

function readMinutesFromDateTime(value?: string | null) {
  if (!value?.trim()) {
    return null
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }

  return parsed.getHours() * 60 + parsed.getMinutes() + parsed.getSeconds() / 60
}

function clipMinutes(value: number) {
  return Math.max(0, Math.min(DAY_MINUTES, value))
}

export function resolveWorkDayEntryWorkDateKey(entry: WorkDayEntry) {
  const fromField = readDateKeyFromValue(entry.work_date)
  if (fromField) {
    return fromField
  }

  const startValue = preferOrgLocalDateTime(entry.planned_start, entry.planned_start_local)
    || preferOrgLocalDateTime(entry.check_in, entry.check_in_local)

  return readDateKeyFromValue(startValue)
}

export function resolveWorkDayEntryTimeBounds(entry: WorkDayEntry) {
  const startValue = preferOrgLocalDateTime(entry.planned_start, entry.planned_start_local)
    || preferOrgLocalDateTime(entry.check_in, entry.check_in_local)
  const endValue = preferOrgLocalDateTime(entry.planned_end, entry.planned_end_local)
    || preferOrgLocalDateTime(entry.check_out, entry.check_out_local)

  const startMinutes = readMinutesFromDateTime(startValue)
  const endMinutes = readMinutesFromDateTime(endValue)

  if (startMinutes === null && endMinutes === null) {
    return null
  }

  const resolvedStart = clipMinutes(startMinutes ?? 0)
  const resolvedEnd = clipMinutes(endMinutes ?? Math.min(resolvedStart + 60, DAY_MINUTES))

  if (resolvedEnd <= resolvedStart) {
    return {
      startMinutes: resolvedStart,
      endMinutes: Math.min(DAY_MINUTES, resolvedStart + 60),
    }
  }

  return {
    startMinutes: resolvedStart,
    endMinutes: resolvedEnd,
  }
}

export function resolveWorkDayEntryCategory(
  entry: WorkDayEntry,
  jobById: Map<string, Job>,
) {
  const job = jobById.get(String(entry.job))
  if (job) {
    const categoryKey = resolveJobCategoryKey(job)
    const categoryLabel = job.category_name?.trim()
      || (categoryKey === 'uncategorized' ? 'Uncategorized' : categoryKey)

    return {
      categoryKey,
      categoryLabel,
    }
  }

  if (entry.worker_skill_label?.trim()) {
    const skillKey = entry.worker_skill != null
      ? `skill:${String(entry.worker_skill)}`
      : `skill:${entry.worker_skill_label.trim().toLowerCase()}`

    return {
      categoryKey: skillKey,
      categoryLabel: entry.worker_skill_label.trim(),
    }
  }

  return {
    categoryKey: 'uncategorized',
    categoryLabel: 'Uncategorized',
  }
}

export function buildWorkDayCategoryTimelineRows(
  entries: WorkDayEntry[],
  jobById: Map<string, Job>,
  weekDateKeys: string[],
) {
  const weekDateSet = new Set(weekDateKeys)
  const rowMap = new Map<string, WorkDayCategoryTimelineRow>()

  for (const entry of entries) {
    const workDateKey = resolveWorkDayEntryWorkDateKey(entry)
    if (!workDateKey || !weekDateSet.has(workDateKey)) {
      continue
    }

    const bounds = resolveWorkDayEntryTimeBounds(entry)
    if (!bounds) {
      continue
    }

    const { categoryKey, categoryLabel } = resolveWorkDayEntryCategory(entry, jobById)
    const existingRow = rowMap.get(categoryKey)
    const row = existingRow ?? {
      categoryKey,
      categoryLabel,
      color: getJobCategoryColor(categoryKey),
      entriesByDate: new Map<string, WorkDayCategoryTimelineEntry[]>(),
    }

    const timelineEntry: WorkDayCategoryTimelineEntry = {
      entry,
      categoryKey,
      categoryLabel,
      workDateKey,
      startMinutes: bounds.startMinutes,
      endMinutes: bounds.endMinutes,
    }

    const dayEntries = row.entriesByDate.get(workDateKey) ?? []
    dayEntries.push(timelineEntry)
    row.entriesByDate.set(workDateKey, dayEntries)
    rowMap.set(categoryKey, row)
  }

  const rows = [...rowMap.values()]
    .map((row) => {
      for (const [dateKey, dayEntries] of row.entriesByDate.entries()) {
        dayEntries.sort((left, right) => {
          if (left.startMinutes !== right.startMinutes) {
            return left.startMinutes - right.startMinutes
          }
          return left.endMinutes - right.endMinutes
        })
        row.entriesByDate.set(dateKey, dayEntries)
      }
      return row
    })
    .sort((left, right) => left.categoryLabel.localeCompare(right.categoryLabel))

  return rows
}

export function countTimelineEntriesByStatus(rows: WorkDayCategoryTimelineRow[]) {
  const counts = new Map<WorkDayEntryStatus, number>()

  for (const row of rows) {
    for (const dayEntries of row.entriesByDate.values()) {
      for (const timelineEntry of dayEntries) {
        const status = resolveWorkDayEntryStatusFromEntry(timelineEntry.entry)
        if (!status) {
          continue
        }
        counts.set(status, (counts.get(status) ?? 0) + 1)
      }
    }
  }

  return counts
}

export function filterTimelineRowsByStatus(
  rows: WorkDayCategoryTimelineRow[],
  hiddenStatuses: ReadonlySet<WorkDayEntryStatus>,
) {
  if (hiddenStatuses.size === 0) {
    return rows
  }

  return rows
    .map((row) => {
      const entriesByDate = new Map<string, WorkDayCategoryTimelineEntry[]>()

      for (const [dateKey, dayEntries] of row.entriesByDate.entries()) {
        const filteredEntries = dayEntries.filter((timelineEntry) => {
          const status = resolveWorkDayEntryStatusFromEntry(timelineEntry.entry)
          return status != null && !hiddenStatuses.has(status)
        })

        if (filteredEntries.length > 0) {
          entriesByDate.set(dateKey, filteredEntries)
        }
      }

      if (entriesByDate.size === 0) {
        return null
      }

      return {
        ...row,
        entriesByDate,
      }
    })
    .filter((row): row is WorkDayCategoryTimelineRow => row != null)
}

export function formatTimelineClock(minutes: number) {
  const clamped = clipMinutes(minutes)
  const hours = Math.floor(clamped / 60)
  const mins = Math.round(clamped % 60)
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`
}

export function minutesToPercent(minutes: number) {
  return (minutes / DAY_MINUTES) * 100
}

export const TIMELINE_HOUR_MARKS = Array.from({ length: 25 }, (_, index) => index)
